import { usePulsesQuery } from "@/app/hooks/use-queries/use_pulses_query"
import { RawHTML } from "@/app/raw_html"
import { formatDateComplete } from "@/app/utils"
import { Button } from "@/components/buttons/button"
import CustomHead from "@/components/layouts/custom_head"
import { Header } from "@/components/layouts/header"
import { Spinner } from "@/components/layouts/spinner"
import { TableauViz } from "@/components/tableau"
import { PulseChart } from "@/pages/pulse/components/pulse_chart"
import Image from "next/image"
import Link from "next/link"
import { useRouter } from "next/router"

export default function PulseDetail() {
  const { id } = useRouter().query
  const pulseId = Array.isArray(id) ? id[0] : id

  const { data: pulses, isLoading, error } = usePulsesQuery()
  const pulse = pulses?.find((pulse) => pulse.key === pulseId)

  if (error)
    return (
      <div className="flex flex-col items-center justify-center">
        <p className="font-semibold">Error Loading Pulses.</p>
      </div>
    )

  if (!pulse || isLoading || !pulses)
    return <Spinner hasTitle title="Loading Pulse Article..." />

  const highcharts = pulse.highcharts ?? pulse

  return (
    <>
      <CustomHead
        title={pulse.title ?? "Pulse Article"}
        content={pulse.content ?? "A pulse article by Grafiniti."}
        nofollow={false}
        noindex={false}
      />

      <div id="container" className="mt-1 flex flex-col">
        <div className="flex flex-col items-center">
          <div className="flex w-full flex-col">
            <Header className="px-2 py-1.5" />

            <div className="flex items-center px-2">
              <Link href="/pulse">
                <Button color="primary" className="!text-base">
                  {pulse.category}
                </Button>
              </Link>
              <div className="flex max-w-24 items-center gap-2 pl-1 sm:max-w-28">
                <p className=" text-main-dark">by</p>
                <Image
                  width={100}
                  height={100}
                  src="/images/grafiniti-logo.png"
                  className="h-6 w-auto"
                  alt="Grafiniti Logo"
                />
              </div>
            </div>

            {/* title and subtitle area */}
            <div className="px-2 pt-1">
              <h3 className="text-lg font-bold">{pulse.title}</h3>
              <p className="text-sm text-main-500">{pulse.subtitle}</p>
              <p className="pt-1 text-sm text-main-500">
                {formatDateComplete(pulse.publishedAt!)}
              </p>
            </div>

            {/* chart and text */}
            <div className="flex flex-col gap-2">
              {pulse.chartNote && (
                <div className="px-2 pt-1">
                  <p className="text-sm text-main-500">{pulse.chartNote}</p>
                </div>
              )}
              {pulse.type && pulse.tableau ?
                <TableauViz
                  slotKey={null}
                  src={pulse.tableau.url}
                  pulseParameters={pulse.tableau.parameters}
                  tableauParameters={{ height: "850", width: "1200" }}
                />
              : <PulseChart
                  {...pulse}
                  disclaimer={pulse.disclaimer}
                  currency={highcharts.chartCurrency}
                  categories={highcharts.chartLabels}
                  data={highcharts.chartSeries}
                  percent={highcharts.chartPercent}
                  stacked={highcharts.chartStacked}
                  stacking={highcharts.chartStacking}
                  type={highcharts.chartType}
                  height={highcharts.chartHeight}
                  chartLabelsFormat={highcharts?.chartLabelsFormat}
                />
              }
            </div>
          </div>

          <div className="pulse-content flex flex-col gap-4 self-start px-4 pt-2 text-sm">
            {pulse && pulse.content ?
              pulse.content.split("\n\n").map((text, index) => (
                <p key={index + text}>
                  <RawHTML>{text}</RawHTML>
                </p>
              ))
            : null}
            <p>&nbsp;</p>
          </div>
        </div>
      </div>
    </>
  )
}
